import { useEffect, useState } from "react";
import { ImageAdapter } from "./adapters/ImageAdapter";
import { PdfCanvasAdapter } from "./pdf/PdfCanvasAdapter";
import { FileNav } from "./shared/FileNav";
import { ViewerToolbar } from "./shared/ViewerToolbar";
import { getDocumentType } from "./utils";
import "./DocumentViewer.css";

type DocumentViewerProps = {
  urls: string[];
  fileName?: string;
  isToolbarActive?: boolean;
  /** Smaller toolbar that fades when the pointer leaves the preview. */
  compact?: boolean;
};

export const DocumentViewer = ({
  urls,
  fileName,
  isToolbarActive,
  compact = false,
}: DocumentViewerProps) => {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => setIndex(0), [urls]);

  if (urls.length === 0) {
    return <div className="doc-viewer doc-viewer--message">No files to display.</div>;
  }

  // compact: follow the pointer unless the parent controls it explicitly.
  // non-compact: identical to the old behaviour (defaults to true).
  const toolbarActive = compact
    ? isToolbarActive ?? isHovered
    : isToolbarActive ?? true;

  const activeIndex = Math.min(index, urls.length - 1);
  const url = urls[activeIndex];
  const nav =
    urls.length > 1 ? (
      <FileNav index={activeIndex} total={urls.length} onChange={setIndex} />
    ) : null;

  const shared = { url, fileName, nav, isToolbarActive: toolbarActive };

  let content;
  switch (getDocumentType(url)) {
    case "pdf":
      content = <PdfCanvasAdapter key={url} {...shared} />;
      break;
    case "image":
      content = <ImageAdapter key={url} {...shared} />;
      break;
    default:
      content = (
        <div className="doc-viewer">
          <ViewerToolbar
            downloadUrl={url}
            fileName={fileName}
            nav={nav}
            isActive={toolbarActive}
          />
          <div className="doc-viewer-stage doc-viewer-stage--message">
            This file format cannot be previewed. Please download the original file
            to view it using an external application.
          </div>
        </div>
      );
  }

  if (!compact) return content;

  return (
    <div
      className="doc-viewer-host doc-viewer-host--compact"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
    </div>
  );
};